import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createIndirectCost, updateIndirectCost, deleteIndirectCost,
  loadDefaultIndirectCosts, clearIndirectCosts,
} from '../../../api/indirectCosts';
import { MOIS_COURT } from '../engine/constants';
import { formatCHF } from '../engine/adapters';
import { useP } from '../../../i18n/useP';

const ALL_MONTHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const parseNum = (s) => {
  if (typeof s === 'number') return s;
  if (!s) return 0;
  const n = parseFloat(String(s).replace(/[\s']/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

function NumInput({ value, onCommit, disabled = false, align = 'right' }) {
  const [draft, setDraft] = useState(null);
  const display = draft !== null
    ? draft
    : (value ? Math.round(value).toLocaleString('de-CH').replace(/\./g, "'") : '');
  return (
    <input
      type="text" inputMode="decimal" value={display} disabled={disabled}
      style={{ textAlign: align }}
      onFocus={() => setDraft(String(value || ''))}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) {
          const parsed = parseNum(draft);
          if (parsed !== value) onCommit(parsed);
        }
        setDraft(null);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

/**
 * Position input — typed by the user as a 1-based row index. Commits only if
 * the parsed value differs from the current position. Clamped to [1, max] by
 * the caller (`reorderTo`).
 */
function PosInput({ value, max, onCommit, disabled = false }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="text"
      inputMode="numeric"
      disabled={disabled}
      value={draft !== null ? draft : String(value)}
      style={{ textAlign: 'center', width: '3em', padding: '2px 2px' }}
      onFocus={e => { setDraft(String(value)); e.target.select(); }}
      onChange={e => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
      onBlur={() => {
        if (draft !== null) {
          const parsed = parseInt(draft, 10);
          if (Number.isFinite(parsed) && parsed !== value) {
            onCommit(Math.max(1, Math.min(max, parsed)));
          }
        }
        setDraft(null);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); else if (e.key === 'Escape') { setDraft(null); e.target.blur(); } }}
    />
  );
}

function TextInput({ value, onCommit, disabled = false }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="text" disabled={disabled}
      value={draft !== null ? draft : (value || '')}
      style={{ textAlign: 'left' }}
      onFocus={() => setDraft(value || '')}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null && draft.trim() !== (value || '')) onCommit(draft.trim());
        setDraft(null);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

function MonthChips({ values = [], onChange, disabled = false }) {
  const p = useP();
  const active = new Set(values);
  const allOn = active.size === 12;
  return (
    <div className="tres-chips">
      <button
        type="button" disabled={disabled}
        className={`tres-chip tres-chip--tous ${allOn ? 'tres-chip--active' : ''}`}
        onClick={() => onChange(allOn ? [0] : [...ALL_MONTHS])}
        title={allOn ? p('Tout décocher') : p('Tout cocher')}
      >
        {p('TOUS')}
      </button>
      {MOIS_COURT.map((m, i) => (
        <button
          key={i} type="button" disabled={disabled}
          className={`tres-chip ${active.has(i) ? 'tres-chip--active' : ''}`}
          onClick={() => {
            const next = new Set(active);
            if (next.has(i)) next.delete(i); else next.add(i);
            const sorted = [...next].sort((a, b) => a - b);
            onChange(sorted.length ? sorted : [i]);
          }}
        >{m.slice(0, 1)}</button>
      ))}
    </div>
  );
}

function totalAnnualOf(row) {
  const amount = Number(row.amount) || 0;
  const nbMonths = (row.active_months || []).length;
  if (nbMonths === 0) return 0;
  return row.mode === 'annual' ? amount : amount * nbMonths;
}
function perOccurrenceOf(row) {
  const amount = Number(row.amount) || 0;
  const nbMonths = (row.active_months || []).length;
  if (nbMonths === 0) return 0;
  return row.mode === 'annual' ? amount / nbMonths : amount;
}

/**
 * Indirect costs tab — edits the central tenant × year table.
 *
 * Year is derived from `scenario.start_year`. All Cash 12M scenarios anchored
 * to that same year share this set of indirect costs (no scenario-level
 * variation, by design — see refactor 2026-05-16).
 */
export default function IndirectTab({ tenantId, scenario, indirectCosts, onChange }) {
  const { t } = useTranslation();
  const p = useP();
  const [error, setError] = useState(null);
  const locked = scenario.locked;
  const year = scenario.start_year;

  async function patch(cid, payload) {
    const r = await updateIndirectCost(tenantId, cid, payload);
    if (r.error) setError(r.error); else { setError(null); onChange(); }
  }

  /**
   * Reorder one row to a target 1-based position. Other rows shift to fill
   * the gap. Only patches rows whose display_order actually changes, then
   * fires onChange() once at the end.
   *
   * Pas d'endpoint batch côté backend — on tire N PATCH en parallèle via
   * Promise.all. À envisager si reorder devient une opération fréquente.
   */
  async function reorderTo(cid, newPos1Based) {
    const current = [...sorted];
    const fromIdx = current.findIndex(r => r.id === cid);
    if (fromIdx < 0) return;
    let toIdx = Math.max(1, Math.min(current.length, newPos1Based)) - 1;
    if (toIdx === fromIdx) return;
    const [moved] = current.splice(fromIdx, 1);
    current.splice(toIdx, 0, moved);
    const ops = [];
    current.forEach((row, idx) => {
      if (row.display_order !== idx) {
        ops.push(updateIndirectCost(tenantId, row.id, { display_order: idx }));
      }
    });
    if (ops.length === 0) return;
    const results = await Promise.all(ops);
    const failed = results.find(r => r.error);
    if (failed) setError(failed.error); else setError(null);
    onChange();
  }
  async function add() {
    const r = await createIndirectCost(tenantId, {
      year,
      label: 'Nouvelle charge',
      amount: 0,
      mode: 'monthly',
      active_months: ALL_MONTHS,
      vat_recoverable: true,
      display_order: indirectCosts.length,
    });
    if (r.error) setError(r.error); else { setError(null); onChange(); }
  }
  async function remove(cid) {
    if (!window.confirm(p('Supprimer cette ligne ?'))) return;
    const r = await deleteIndirectCost(tenantId, cid);
    if (r.error) setError(r.error); else { setError(null); onChange(); }
  }
  async function handleLoadDefaults() {
    if (indirectCosts.length > 0) {
      const ok = window.confirm(
        `${indirectCosts.length} ${p('ligne(s) existent déjà pour')} ${year}. ${p('Ajouter les 18 lignes par défaut en plus ? (Les existantes ne seront pas supprimées.)')}`
      );
      if (!ok) return;
    }
    const r = await loadDefaultIndirectCosts(tenantId, year);
    if (r.error) setError(r.error); else { setError(null); onChange(); }
  }
  async function handleClearAll() {
    if (indirectCosts.length === 0) return;
    const ok = window.confirm(
      `${p('Vider toutes les charges indirectes pour')} ${year} (${indirectCosts.length} ${p('ligne(s)')}) ? ${p('Cette action est irréversible et impacte tous les scénarios sur')} ${year}.`
    );
    if (!ok) return;
    const r = await clearIndirectCosts(tenantId, year);
    if (r.error) setError(r.error); else { setError(null); onChange(); }
  }

  const sorted = useMemo(() => {
    return [...indirectCosts].sort((a, b) => {
      if (a.display_order !== b.display_order) return a.display_order - b.display_order;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [indirectCosts]);

  const totalAnnuel = sorted.reduce((acc, r) => acc + totalAnnualOf(r), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h2 className="tres-section-title">{p('Charges indirectes')}</h2>
          <p className="tres-section-subtitle">
            {p('Table centrale partagée par tous les scénarios Cash 12M sur l\'année')} {year}.
            {' '}{p('Loyers, abonnements, fournitures, honoraires…')}
            {' '}{p('Mode / mois = montant identique chaque mois actif.')}
            {' '}{p('Mode / an = montant annuel réparti sur les mois actifs.')}
          </p>
        </div>
        {!locked && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
            <button className="tres-bar__btn" onClick={handleLoadDefaults} style={{ whiteSpace: 'nowrap' }}>
              {p('📋 Charger 18 charges par défaut')}
            </button>
            <button
              className="tres-bar__btn tres-bar__btn--danger"
              onClick={handleClearAll}
              disabled={indirectCosts.length === 0}
              style={{ whiteSpace: 'nowrap' }}
            >
              {p("🗑 Vider l'année")}
            </button>
          </div>
        )}
      </div>

      {error && <div className="treasury__error">{error} <button onClick={() => setError(null)}>×</button></div>}

      <div className="tres-indirect-card">
      <table className="tres-table tres-edit-table tres-indirect-table">
        <thead>
          <tr>
            <th className="col-pos" title={t('phrases:p_9c94162c8d')}>#</th>
            <th className="col-label">{t('phrases:p_f5485bbae8')}</th>
            <th className="col-amount">{p('Montant HT')}</th>
            <th className="col-mode">{p('Mode')}</th>
            <th className="col-vat">{p('TVA récup.')}</th>
            <th className="col-months">{p('Mois de paiement')}</th>
            <th className="col-occ">{p('Par occ.')}</th>
            <th className="col-total">{p('Total / an')}</th>
            <th className="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, idx) => (
            <tr key={r.id}>
              <td className="col-pos">
                <PosInput
                  value={idx + 1}
                  max={sorted.length}
                  disabled={locked}
                  onCommit={pos => reorderTo(r.id, pos)}
                />
              </td>
              <td className="col-label">
                <TextInput value={r.label} disabled={locked} onCommit={v => patch(r.id, { label: v })} />
              </td>
              <td className="col-amount">
                <NumInput value={Number(r.amount)} disabled={locked} onCommit={v => patch(r.id, { amount: v })} />
              </td>
              <td className="col-mode">
                <select value={r.mode} disabled={locked}
                  onChange={e => patch(r.id, { mode: e.target.value })}>
                  <option value="monthly">{p('/ mois')}</option>
                  <option value="annual">{p('/ an')}</option>
                </select>
              </td>
              <td className="col-vat" style={{ textAlign: 'center' }}>
                <input type="checkbox" checked={!!r.vat_recoverable} disabled={locked}
                  onChange={e => patch(r.id, { vat_recoverable: e.target.checked })} />
              </td>
              <td className="col-months">
                <MonthChips
                  values={r.active_months || []} disabled={locked}
                  onChange={ms => patch(r.id, { active_months: ms })}
                />
              </td>
              <td className="col-occ tres-num">
                {Math.round(perOccurrenceOf(r)).toLocaleString('de-CH').replace(/\./g, "'")}
              </td>
              <td className="col-total tres-num tres-total-cell">
                {Math.round(totalAnnualOf(r)).toLocaleString('de-CH').replace(/\./g, "'")}
              </td>
              <td className="col-actions">
                <button disabled={locked} onClick={() => remove(r.id)} title={t('common:actions.delete')}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="tres-indirect-footer">
            <td colSpan={7} style={{ textAlign: 'right', fontWeight: 600 }}>{p('TOTAL')}</td>
            <td className="tres-num tres-total-cell"><strong>{formatCHF(totalAnnuel, { decimals: 0, currency: '' }).trim()}</strong></td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      </div>

      {!locked && <button className="tres-add-row" onClick={add}>{p('+ Ajouter une charge indirecte')}</button>}
    </div>
  );
}
